import { customerPhoneStringToDisplay } from "@jugl-web/domain-resources/customers/utils/customerPhoneStringToDisplay";
import type { ClientContact } from "@jugl-web/rest-api/contacts";
import { parsePhoneNumber } from "libphonenumber-js";
import type {
  ContactInfoData,
  ContactInfoFieldValue,
} from "./components/ContactIntelligenceInfoSection/types";

export const formatContactPhoneForDisplay = (
  raw: string | null | undefined
): string | null => {
  if (typeof raw !== "string") {
    return null;
  }
  let display: string | undefined;
  let isValid = false;
  try {
    isValid = parsePhoneNumber(raw.startsWith("+") ? raw : `+${raw}`).isValid();
  } catch {
    // skip
  }
  if (isValid) {
    display = customerPhoneStringToDisplay(raw).trim();
  }
  return display || raw;
};

const toFieldValue = (raw?: string | null): ContactInfoFieldValue | null => {
  if (!raw) {
    return null;
  }
  if (/^https?:\/\//i.test(raw)) {
    return { type: "link", value: raw, href: raw };
  }
  return { type: "plain", value: raw };
};

const toFieldValueOrDash = (raw?: string | null): ContactInfoFieldValue =>
  toFieldValue(raw) ?? { type: "dash" };

const toConfidenceScore = (
  score?: number | null
): number | null | undefined => {
  if (score == null) {
    return score;
  }

  return Math.min(10, Math.max(0, Math.round(score * 100) / 10));
};

const getProfilePhone = (contact: Partial<ClientContact>) =>
  formatContactPhoneForDisplay(contact.phone);

const getProfileEmail = (contact: Partial<ClientContact>) => contact.email;

const getVisitorIntelligencePhone = (contact: Partial<ClientContact>) =>
  formatContactPhoneForDisplay(contact.rb2b?.mobile);

const getVisitorIntelligencePersonalEmail = (contact: Partial<ClientContact>) =>
  contact.rb2b?.email;

const getVisitorIntelligenceBusinessEmail = (contact: Partial<ClientContact>) =>
  contact.rb2b?.info?.business_email;

const getVisitorIntelligenceCountry = (contact: Partial<ClientContact>) =>
  contact.rb2b?.info?.country;

const getVisitorIntelligenceLinkedIn = (contact: Partial<ClientContact>) =>
  contact.rb2b?.info?.linkedin_url;

const getVisitorIntelligenceTitle = (contact: Partial<ClientContact>) =>
  contact.rb2b?.info?.title;

const getVisitorIntelligenceCompanyName = (contact: Partial<ClientContact>) =>
  contact.rb2b?.info?.company?.name;

const getVisitorIntelligenceCompanyRevenue = (
  contact: Partial<ClientContact>
) => contact.rb2b?.info?.company?.revenue;

const getVisitorIntelligenceIndustry = (contact: Partial<ClientContact>) =>
  contact.rb2b?.info?.industry;

const getVisitorIntelligenceCompanyWebsite = (
  contact: Partial<ClientContact>
) => contact.rb2b?.info?.company?.website_url;

const getVisitorIntelligenceCompanyLinkedIn = (
  contact: Partial<ClientContact>
) => contact.rb2b?.info?.company?.linkedin_url;

const collectVisitorIntelligenceDataPointValues = (
  contact: Partial<Pick<ClientContact, "rb2b">>
): string[] => {
  const dataPoints = [
    getVisitorIntelligencePhone(contact),
    getVisitorIntelligencePersonalEmail(contact),
    getVisitorIntelligenceBusinessEmail(contact),
    getVisitorIntelligenceCountry(contact),
    getVisitorIntelligenceLinkedIn(contact),
    getVisitorIntelligenceCompanyName(contact),
    getVisitorIntelligenceCompanyRevenue(contact),
    getVisitorIntelligenceIndustry(contact),
    getVisitorIntelligenceTitle(contact),
    getVisitorIntelligenceCompanyWebsite(contact),
    getVisitorIntelligenceCompanyLinkedIn(contact),
  ].filter((value): value is string => Boolean(value));

  return [...new Set(dataPoints)];
};

export const getVisitorIntelligenceDataPointsSnapshot = (
  contact: Partial<Pick<ClientContact, "rb2b">>
): string[] => [...collectVisitorIntelligenceDataPointValues(contact)].sort();

export const getVisitorIntelligenceDataPointsCount = (
  contact: Partial<Pick<ClientContact, "rb2b">>
): number => collectVisitorIntelligenceDataPointValues(contact).length;

export const hasVisitorIntelligenceData = (
  contact: Partial<Pick<ClientContact, "rb2b">>
): boolean => getVisitorIntelligenceDataPointsCount(contact) > 0;

export type ContactInfoDataVariant = "visitorIntelligence" | "info";

export const mapContactToContactInfoData = (
  contact: Partial<ClientContact>
): ContactInfoData => {
  return {
    profile: {
      phoneNumber: toFieldValueOrDash(getProfilePhone(contact)),
      email: toFieldValueOrDash(getProfileEmail(contact)),
      info: Object.fromEntries(
        Object.entries(contact.info ?? {}).map(([fieldKey, raw]) => [
          fieldKey,
          toFieldValue(formatContactPhoneForDisplay(raw)),
        ])
      ),
    },
    visitorIntelligence: {
      phoneNumber: toFieldValueOrDash(getVisitorIntelligencePhone(contact)),
      personalEmail: toFieldValueOrDash(
        getVisitorIntelligencePersonalEmail(contact)
      ),
      businessEmail: toFieldValueOrDash(
        getVisitorIntelligenceBusinessEmail(contact)
      ),
      location: toFieldValue(getVisitorIntelligenceCountry(contact)),
      linkedin: toFieldValue(getVisitorIntelligenceLinkedIn(contact)),
      company: toFieldValue(getVisitorIntelligenceCompanyName(contact)),
      revenue: toFieldValue(getVisitorIntelligenceCompanyRevenue(contact)),
      industry: toFieldValue(getVisitorIntelligenceIndustry(contact)),
      title: toFieldValue(getVisitorIntelligenceTitle(contact)),
      website: toFieldValue(getVisitorIntelligenceCompanyWebsite(contact)),
      companyLinkedin: toFieldValue(
        getVisitorIntelligenceCompanyLinkedIn(contact)
      ),
      confidence: toConfidenceScore(contact.rb2b?.score),
    },
  };
};

export const getContactConversationId = (
  contact: ClientContact
): string | null => contact.conversations[0]?.conv_id ?? null;

export const getContactLeadSources = (contact: ClientContact): string[] => {
  const seen = new Set<string>();

  return contact.conversations
    .map(({ src }) => src.trim())
    .filter((src) => {
      if (!src || seen.has(src)) {
        return false;
      }

      seen.add(src);
      return true;
    });
};

export const getContactAdditionalFieldValue = (
  contact: ClientContact,
  fieldKey: string
) => {
  const topLevel = contact[fieldKey as keyof ClientContact];
  if (typeof topLevel === "string") {
    return topLevel;
  }

  const infoValue = contact.info[fieldKey];
  if (typeof infoValue === "string") {
    return infoValue;
  }

  return "";
};
